import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import { Error, Add, Send, DriveFileRenameOutline } from '@mui/icons-material';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { RefereeStatus } from './types';
import { PAGE_TEXT } from './lang';
import {
  useQueryAllRefereeStatus,
  useResendRefereeEmail,
  useAddNewReferee,
  useChangeAcademicRefereeEmail,
  type AcademicRefFormData,
  type RefereeReportStatus,
} from '../../../graphql';
import {
  type HeadCell,
  Table,
  Alert,
  type AlertDetails,
  Dialog,
  type DialogDetails,
  TextField,
  IconButton,
  Button,
  Loading,
} from '../../../../../components';
import { AcademicRefBox } from '../../ApplicationForm';
import { handlePageLoading } from '../../../../../utils';

const MAX_REFEREES_LIMIT = 5;
type CustomizedDialogType = Omit<DialogDetails, 'content'> & {
  width?: string;
  content: React.ReactNode;
};

const EmailChangeForm: React.FC<{
  handleCancel: () => void;
  handleChangeEmail: (email: string) => Promise<void>;
}> = ({ handleCancel, handleChangeEmail }) => {
  const emailFormik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: object({
      email: string().email().required('Required'),
    }),
    onSubmit: async values => {
      await handleChangeEmail(values.email);
    },
  });

  return (
    <form
      style={{
        padding: '1rem',
      }}
      onSubmit={emailFormik.handleSubmit}
    >
      <TextField
        label={PAGE_TEXT.information.newEmail}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{
          height: '5rem',
        }}
        fullWidth
        type="email"
        name="email"
        value={emailFormik.values.email}
        onChange={emailFormik.handleChange}
        onBlur={emailFormik.handleBlur}
        error={emailFormik.touched.email && Boolean(emailFormik.errors.email)}
        helperText={emailFormik.touched.email && emailFormik.errors.email}
      />
      <div className="w-full flex flex-row items-center justify-center gap-4 my-4">
        <Button
          variant="contained"
          type="submit"
          sx={{
            textTransform: 'none',
          }}
        >
          {PAGE_TEXT.refereeAction.CHANGE}
        </Button>
        <Button
          sx={{
            textTransform: 'none',
          }}
          onClick={handleCancel}
        >
          {PAGE_TEXT.refereeAction.CANCEL}
        </Button>
      </div>
    </form>
  );
};

export const RefereeStatusTable: React.FC = () => {
  const { applicationId } = useParams();
  const [openDialog, setOpenDialog] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [dialogDetails, setDialogDetails] = useState<CustomizedDialogType>({
    title: '',
    content: '',
  });

  const headCells: Array<
    HeadCell<RefereeReportStatus & { action?: React.ReactNode }>
  > = [
    {
      id: 'nameOfReferee',
      label: PAGE_TEXT.refereeFormHeader.NAME,
      isSortable: false,
      render: (row: any) => row.nameOfReferee,
    },
    {
      id: 'email',
      label: PAGE_TEXT.refereeFormHeader.EMAIL,
      isSortable: false,
      render: (row: any) => row.email,
    },
    {
      id: 'status',
      label: PAGE_TEXT.refereeFormHeader.STATUS,
      isSortable: false,
      render: (row: any) => PAGE_TEXT.refereeStatus[row.status],
    },
    {
      id: 'lastSentDate',
      label: PAGE_TEXT.refereeFormHeader.LAST_SENT_TIME,
      isSortable: false,
      render: (row: any) => row.lastSentDate,
    },
    {
      id: 'repliedAt',
      label: PAGE_TEXT.refereeFormHeader.REPLY_TIME,
      isSortable: false,
      render: (row: any) => row.repliedAt,
    },
    {
      id: 'deadline',
      label: PAGE_TEXT.refereeFormHeader.DEADLINE,
      isSortable: false,
      render: (row: any) => row.deadline,
    },
    {
      id: 'action',
      label: PAGE_TEXT.refereeFormHeader.ACTION,
      isSortable: false,
      minWidth: 12,
      render: (row: any) => action(row),
    },
  ];

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertContent: '',
    alertType: 'error',
  });

  const {
    refereeReportStatus = [],
    loading,
    error,
    refetch,
  } = useQueryAllRefereeStatus(applicationId as string);

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const resendRefereeEmail = useResendRefereeEmail();
  const handleResendRefereeEmail = async (
    academicRefereeId: string,
  ): Promise<void> => {
    await handlePageLoading(setPageLoading, async () => {
      try {
        await resendRefereeEmail({
          variables: {
            applicationId,
            academicRefereeId,
          },
        });
        setAlertDetails({
          alertContent: 'Resend Email Successfully.',
          alertType: 'success',
        });
        refetch();
      } catch (e) {
        setAlertDetails({
          alertContent: `Resend Email Failed.${(e as Error)?.message}`,
          alertType: 'error',
        });
      } finally {
        setOpenAlert(true);
        setOpenDialog(false);
      }
    });
  };

  const handleOpenResendEmailDialog = (row: any): void => {
    setDialogDetails({
      ...dialogMappings.resendEmail,
      handleDialogConfirmAction: async () => {
        await handleResendRefereeEmail(row.academicRefereeId);
      },
    });
    setOpenDialog(true);
  };

  const addNewReferee = useAddNewReferee();
  const handleAddNewReferee = async (
    values: AcademicRefFormData,
  ): Promise<void> => {
    await handlePageLoading(setPageLoading, async () => {
      try {
        const { surname, givenName, ...others } = values;
        await addNewReferee({
          variables: {
            applicationId,
            input: {
              name: {
                surname,
                givenName,
              },
              ...others,
            },
          },
        });
        setAlertDetails({
          alertContent: 'Saved Academic Referee Successfully.',
          alertType: 'success',
        });
        refetch();
      } catch (e) {
        setAlertDetails({
          alertContent: `Saved Academic Referee Failed.${(e as Error)?.message}`,
          alertType: 'error',
        });
      } finally {
        setOpenAlert(true);
        setOpenDialog(false);
      }
    });
  };

  const action = (row: RefereeReportStatus): React.ReactNode => {
    const { status, canResendEmail, canUpdateEmail } = row;
    const changeEmailDisable =
      status === RefereeStatus.SUBMITTED || !canUpdateEmail;
    const resendDisable = status === RefereeStatus.SUBMITTED || !canResendEmail;
    return (
      <div className="flex flex-row item-center justify-center ">
        <Tooltip title={PAGE_TEXT.refereeAction.CHANGE_EMAIL}>
          <>
            <IconButton
              icon={
                <DriveFileRenameOutline
                  color={changeEmailDisable ? 'disabled' : 'primary'}
                />
              }
              disabled={changeEmailDisable}
              onClick={() => {
                handleChangeEmailButtonClick(row);
              }}
            />
          </>
        </Tooltip>
        <Tooltip title={PAGE_TEXT.refereeAction.RESEND}>
          <>
            <IconButton
              icon={<Send color={resendDisable ? 'disabled' : 'primary'} />}
              disabled={resendDisable}
              onClick={() => {
                handleOpenResendEmailDialog(row);
              }}
            />
          </>
        </Tooltip>
      </div>
    );
  };

  const handleChangeEmailButtonClick = (row: any): void => {
    setDialogDetails({
      title: PAGE_TEXT.information.changeEmail,
      content: (
        <EmailChangeForm
          handleCancel={handleCloseDialog}
          handleChangeEmail={handleChangeEmail(row.academicRefereeId)}
        />
      ),
    });
    setOpenDialog(true);
  };

  const handleOpenRefereeForm = (): void => {
    setDialogDetails(dialogMappings.addReferee);
    setOpenDialog(true);
  };

  const changeEmailAddress = useChangeAcademicRefereeEmail();
  const handleChangeEmail =
    (academicRefereeId: string) =>
    async (email: string): Promise<void> => {
      await handlePageLoading(setPageLoading, async () => {
        try {
          await changeEmailAddress({
            variables: {
              input: {
                applicationId,
                applicationRefereeId: academicRefereeId,
                email,
              },
            },
          });
          setAlertDetails({
            alertContent:
              'Changed email address successfully. The email has been sent to the new email address.',
            alertType: 'success',
          });
          refetch();
          setOpenDialog(false);
        } catch (e) {
          setAlertDetails({
            alertContent: `Changed Email Address Failed.${(e as Error)?.message}`,
            alertType: 'error',
          });
        } finally {
          setOpenAlert(true);
        }
      });
    };

  const handleCloseDialog = (): void => {
    setOpenDialog(false);
  };

  const academicRefProps = {
    initialAcademicRefValues: {
      title: '',
      surname: '',
      givenName: '',
      position: '',
      institution: '',
      relationship: '',
      email: '',
      phoneNumber: '',
      faxNumber: '',
    },
    academicRefIndex: 0,
    enableEdit: true,
    actionArea: (
      <div className="w-full flex flex-row items-center justify-center gap-4 p-4 mt-2">
        <Button
          variant="contained"
          type="submit"
          sx={{
            textTransform: 'none',
          }}
        >
          {PAGE_TEXT.refereeAction.SAVE_REFEREE}
        </Button>
        <Button
          sx={{
            textTransform: 'none',
          }}
          onClick={handleCloseDialog}
        >
          {PAGE_TEXT.refereeAction.CANCEL}
        </Button>
      </div>
    ) as React.ReactNode,
    handleSubmit: handleAddNewReferee,
  };

  const dialogMappings: Record<string, CustomizedDialogType> = {
    resendEmail: {
      title: PAGE_TEXT.information.resendEmail,
      content: (
        <div className="px-6 mb-4">
          <p>{PAGE_TEXT.information.resendEmailConfirm}</p>
        </div>
      ),
      confirmAction: PAGE_TEXT.refereeAction.RESEND,
      cancelAction: PAGE_TEXT.refereeAction.CANCEL,
    },
    addReferee: {
      title: PAGE_TEXT.information.addReferee,
      content: (
        <div className="px-4">
          <AcademicRefBox {...academicRefProps} />
        </div>
      ),
      width: '50rem',
    },
  };

  const refereeFormHelperText = (
    <HelperText>
      <Error sx={{ color: '#951d1d' }} />
      <p>{PAGE_TEXT.information.headerNote}</p>
    </HelperText>
  );

  const isAllowToAddReferee = refereeReportStatus.length < MAX_REFEREES_LIMIT;
  return (
    <>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      {openDialog && (
        <Dialog
          dialogTitle={dialogDetails.title}
          onDialogClose={handleCloseDialog}
          onDialogAction={dialogDetails.handleDialogConfirmAction}
          openDialog={openDialog}
          dialogAction={dialogDetails.confirmAction}
          cancelAction={dialogDetails.cancelAction}
          width={dialogDetails.width}
        >
          {dialogDetails.content}
        </Dialog>
      )}

      {pageLoading && <Loading />}
      {loading ? (
        <Loading />
      ) : (
        <Container>
          {refereeFormHelperText}
          <Table
            data={refereeReportStatus}
            handleRequestSort={() => {}}
            headCells={headCells}
            height="auto"
          />
          {isAllowToAddReferee && (
            <div className="w-full flex flex-col mt-6">
              <div className="flex flex-row-reverse items-center text-[#951d1d] font-medium">
                {PAGE_TEXT.information.addReferee}
                <IconButton
                  icon={
                    <Add
                      sx={{
                        backgroundColor: '#951d1d',
                        color: '#fff',
                        borderRadius: '50%',
                      }}
                    />
                  }
                  onClick={handleOpenRefereeForm}
                />
              </div>
              <div className="flex flex-row-reverse font-medium">
                {PAGE_TEXT.information.maxReferee}
              </div>
            </div>
          )}
        </Container>
      )}
    </>
  );
};

const Container = styled.div`
  width: 100%;
`;

const HelperText = styled.div`
  width: 100%;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 1rem;

  p {
    margin-left: 0.5rem;
    font-size: 1rem;
  }
`;
